/**
 * COP Dashboard - Dwell Tab Component
 * Displays device dwell locations sorted by start_time descending with timeline chart
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Slider,
  TablePagination,
  useTheme,
} from '@mui/material';
import {
  AccessTime as DwellIcon,
  AccessTime,
  LocationOn as LocationIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { copDwellsService, DeviceDwell } from '../services/copDwellsService';
import { getDeviceColor } from '../utils/deviceColors';
import { parseBackendDateTime } from '@signals/app-shell/utils/parseDateTime';
import {
  localDatePartsToYmd,
  utcInstantToYmdInTimeZone,
  ymdInTimeZoneToUtcInterval,
} from '@signals/app-shell/utils/time';

interface DwellTabProps {
  selectedCourse: string;
  selectedDeviceType: string;
  selectedEntityType?: string;
  selectedEntity?: string;
  selectedDate?: string | null;
  timeZone?: string;
  onDwellSelect?: (dwell: DeviceDwell) => void;
  devicesInBounds?: string[];
}

const DwellTab = ({
  selectedCourse,
  selectedDeviceType,
  selectedEntityType,
  selectedEntity,
  selectedDate,
  timeZone,
  onDwellSelect,
  devicesInBounds
}: DwellTabProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;
  
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '76, 175, 80';
  };
  const primaryRgb = hexToRgb(primaryColor);

  const colors = {
    cardBg: isDark
      ? 'linear-gradient(135deg, rgba(30, 32, 40, 0.98), rgba(24, 26, 32, 0.99))'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(245, 247, 250, 0.95))',
    border: isDark ? `rgba(${primaryRgb}, 0.25)` : `rgba(${primaryRgb}, 0.3)`,
    headerBg: isDark ? `rgba(${primaryRgb}, 0.05)` : `rgba(${primaryRgb}, 0.05)`,
    text: isDark ? 'white' : '#1a1a2e',
    textSecondary: isDark ? '#9e9e9e' : '#666',
    textTertiary: isDark ? '#757575' : '#999',
  };

  const [dwells, setDwells] = useState<DeviceDwell[]>([]);
  const [filteredDwells, setFilteredDwells] = useState<DeviceDwell[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDwell, setSelectedDwell] = useState<DeviceDwell | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [minDurationMin, setMinDurationMin] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Convert selectedDate string to days (must be integer >= 1 for backend API)
  const getDaysFromTimeframe = (timeframe: string | null | undefined): number => {
    if (!timeframe) return 7; // Default to 7 days
    
    switch (timeframe) {
      case 'Last Hour':
      case 'Last 8 Hours':
      case 'Last 12 Hours':
      case 'Last 24 Hours':
        return 1; // Backend expects integer, minimum 1 day
      case 'Last 48 Hours':
        return 2;
      case 'Last 72 Hours':
        return 3;
      case 'Last Week':
        return 7;
      default:
        // Check if it's a date string (YYYY-MM-DD)
        if (timeframe.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // For specific dates, calculate days from now
          const date = new Date(timeframe);
          const now = new Date();
          const diffTime = Math.abs(now.getTime() - date.getTime());
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          return Math.max(1, diffDays);
        }
        return 7;
    }
  };

  const days = getDaysFromTimeframe(selectedDate);
  const selectedDateUtcBounds = useMemo(
    () => selectedDate?.match(/^\d{4}-\d{2}-\d{2}$/) && timeZone
      ? ymdInTimeZoneToUtcInterval(selectedDate, timeZone)
      : null,
    [selectedDate, timeZone],
  );

  const loadDwells = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await copDwellsService.getDwells(
        selectedCourse,
        selectedDeviceType,
        selectedEntityType,
        days,
        100,
        undefined,
        selectedDateUtcBounds || undefined
      );
      setDwells(response.dwells);
    } catch (err) {
      setError('Failed to load dwells');
    } finally {
      setLoading(false);
    }
  }, [selectedCourse, selectedDeviceType, selectedEntityType, days, selectedDateUtcBounds]);

  useEffect(() => {
    loadDwells();
  }, [loadDwells]);

  useEffect(() => {
    let filtered = dwells;
    if (selectedEntity && selectedEntity !== 'All') {
      filtered = filtered.filter(dwell => dwell.entity_id === selectedEntity);
    }
    if (devicesInBounds && devicesInBounds.length > 0) {
      filtered = filtered.filter(dwell => devicesInBounds.includes(dwell.device_id));
    }
    if (selectedDay) {
      filtered = filtered.filter(dwell => utcInstantToYmdInTimeZone(dwell.start_time, timeZone || 'UTC') === selectedDay);
    }
    if (minDurationMin > 0) {
      filtered = filtered.filter(dwell => (dwell.duration_seconds || 0) >= minDurationMin * 60);
    }
    filtered.sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());
    setFilteredDwells(filtered);
  }, [dwells, devicesInBounds, selectedEntity, selectedDay, minDurationMin, timeZone]);

  const handleDwellClick = (dwell: DeviceDwell) => {
    // Toggle selection: if clicking the same dwell, deselect it
    if (selectedDwell?.id === dwell.id) {
      setSelectedDwell(null);
      if (onDwellSelect) onDwellSelect(null as any);
    } else {
      setSelectedDwell(dwell);
      if (onDwellSelect) onDwellSelect(dwell);
    }
  };


  // Color dwell chips with the shared device palette so they match the map
  // pin and the device list. Student dwells split by device_type
  // (phone=green, watch=blue, vehicle=purple); RP/SRP keep role color.
  // DeviceDwell only carries entity_id + device_type (no entity_type), and
  // that's enough for classifyDeviceColor.
  const getEntityColor = (dwell: Pick<DeviceDwell, 'entity_id' | 'device_type'>): string =>
    getDeviceColor(dwell);

  // const formatTimestamp = (isoString: string): string => {
  //   try {
  //     return new Date(isoString).toLocaleString();
  //   } catch {
  //     return isoString;
  //   }
  // };

  const formatDuration = (seconds: number): string => {
    return copDwellsService.formatDuration(seconds);
  };

  // Timeline data - group dwells by day based on selected timeframe
  const timelineData = useMemo(() => {
    const daysArray: { date: string; label: string; dwells: DeviceDwell[]; totalDuration: number }[] = [];
    const now = new Date();
    
    // Calculate number of days to show based on timeframe
    // For sub-day timeframes, show at least 2 days (today and yesterday)
    const numDays = Math.max(2, Math.min(7, Math.ceil(days)));
    
    // Create days array based on selected timeframe
    const todayInTimeZone = timeZone
      ? utcInstantToYmdInTimeZone(now.toISOString(), timeZone)
      : localDatePartsToYmd(now);
    const [todayYear, todayMonth, todayDay] = todayInTimeZone.split('-').map(Number);
    const todayLocal = new Date(todayYear, todayMonth - 1, todayDay);
    for (let i = numDays - 1; i >= 0; i--) {
      const date = new Date(todayLocal);
      date.setDate(date.getDate() - i);
      const dateStr = localDatePartsToYmd(date);
      const dayLabel = i === 0 ? 'Today' : i === 1 ? 'Yesterday' : date.toLocaleDateString('en-US', { weekday: 'short' });
      daysArray.push({ date: dateStr, label: dayLabel, dwells: [], totalDuration: 0 });
    }
    
    // Group dwells by day
    filteredDwells.forEach(dwell => {
      const dwellDate = utcInstantToYmdInTimeZone(dwell.start_time, timeZone || 'UTC');
      const day = daysArray.find(d => d.date === dwellDate);
      if (day) {
        day.dwells.push(dwell);
        day.totalDuration += dwell.duration_seconds || 0;
      }
    });
    
    return daysArray;
  }, [filteredDwells, days, timeZone]);

  // Calculate stats
  const stats = useMemo(() => {
    const totalDuration = filteredDwells.reduce((sum, d) => sum + (d.duration_seconds || 0), 0);
    const avgDuration = filteredDwells.length > 0 ? totalDuration / filteredDwells.length : 0;
    const maxDuration = filteredDwells.reduce((max, d) => Math.max(max, d.duration_seconds || 0), 0);
    const uniqueEntities = new Set(filteredDwells.map(d => d.entity_id)).size;
    
    return { totalDuration, avgDuration, maxDuration, uniqueEntities };
  }, [filteredDwells]);

  useEffect(() => {
    setPage(0);
  }, [filteredDwells.length, selectedDay, minDurationMin, selectedCourse, selectedEntity]);

  const dwellCount = filteredDwells.length;
  const safePage = Math.min(page, Math.max(0, Math.ceil(dwellCount / rowsPerPage) - 1 || 0));
  const pagedDwells = useMemo(
    () => filteredDwells.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [filteredDwells, safePage, rowsPerPage],
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Compact Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
        px: 1.5, py: 1,
        borderRadius: 2, border: `1px solid ${colors.border}`,
        background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
        mb: 1,
      }}>
        <DwellIcon sx={{ color: primaryColor, fontSize: 16 }} />
        <Typography sx={{ color: colors.text, fontWeight: 600, fontSize: '0.8rem' }}>Dwells</Typography>
        <Typography sx={{ color: colors.textTertiary, fontSize: '0.65rem' }}>
          {filteredDwells.length} results
        </Typography>
        <Box sx={{ width: 1, height: 14, bgcolor: colors.border }} />
        <Typography sx={{ color: colors.textTertiary, fontSize: '0.65rem' }}>
          {stats.uniqueEntities} entities
        </Typography>
        <Box sx={{ width: 1, height: 14, bgcolor: colors.border }} />
        <Typography sx={{ color: colors.textTertiary, fontSize: '0.65rem' }}>
          avg <span style={{ color: colors.text, fontWeight: 600 }}>{formatDuration(Math.round(stats.avgDuration))}</span>
        </Typography>
        <Box sx={{ width: 1, height: 14, bgcolor: colors.border }} />
        <Typography sx={{ color: colors.textTertiary, fontSize: '0.65rem' }}>
          total <span style={{ color: primaryColor, fontWeight: 600 }}>{formatDuration(stats.totalDuration)}</span>
        </Typography>
        {devicesInBounds && devicesInBounds.length > 0 && (
          <>
            <Box sx={{ width: 1, height: 14, bgcolor: colors.border }} />
            <Chip icon={<LocationIcon sx={{ fontSize: 12 }} />} label={`${devicesInBounds.length} in view`} size="small"
              sx={{ height: 20, fontSize: '0.6rem', bgcolor: 'rgba(33,150,243,0.08)', color: '#64b5f6', border: '1px solid rgba(33,150,243,0.15)' }} />
          </>
        )}
        {selectedDay && (
          <>
            <Box sx={{ width: 1, height: 14, bgcolor: colors.border }} />
            <Chip icon={<TimelineIcon sx={{ fontSize: 12 }} />} label={timelineData.find(d => d.date === selectedDay)?.label || 'Day'} size="small"
              onDelete={() => setSelectedDay(null)}
              sx={{ height: 20, fontSize: '0.6rem', bgcolor: 'rgba(76,175,80,0.08)', color: '#66bb6a', border: '1px solid rgba(76,175,80,0.15)' }} />
          </>
        )}
      </Box>

      {/* Duration filter slider */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.4, mb: 0.5 }}>
        <Typography sx={{ color: colors.textTertiary, fontSize: '0.6rem', whiteSpace: 'nowrap' }}>Min</Typography>
        <Slider
          value={minDurationMin}
          onChange={(_e, val) => setMinDurationMin(val as number)}
          min={0}
          max={240}
          step={5}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => v === 0 ? 'All' : v < 60 ? `${v}m` : `${Math.floor(v / 60)}h${v % 60 ? ` ${v % 60}m` : ''}`}
          sx={{
            height: 4,
            color: primaryColor,
            '& .MuiSlider-thumb': { width: 12, height: 12 },
            '& .MuiSlider-rail': { opacity: 0.2 },
            '& .MuiSlider-valueLabel': { fontSize: 10, py: 0.2, px: 0.6 },
          }}
        />
        <Typography sx={{ color: minDurationMin > 0 ? primaryColor : colors.textTertiary, fontSize: '0.6rem', whiteSpace: 'nowrap', fontWeight: minDurationMin > 0 ? 600 : 400, minWidth: 28 }}>
          {minDurationMin === 0 ? 'All' : minDurationMin < 60 ? `${minDurationMin}m+` : `${Math.floor(minDurationMin / 60)}h${minDurationMin % 60 ? `${minDurationMin % 60}m` : ''}+`}
        </Typography>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress size={32} sx={{ color: primaryColor }} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ mb: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)', color: colors.text, border: '1px solid rgba(244, 67, 54, 0.3)', borderRadius: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Main Content - Full Width */}
      {!loading && !error && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Full-width Dwells List */}
          <Paper sx={{ 
            flex: 1,
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <List sx={{ 
              flex: 1, 
              overflow: 'auto', 
              p: 1,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
                borderRadius: 4,
              },
            }}>
              {filteredDwells.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <DwellIcon sx={{ fontSize: 48, color: colors.textTertiary, mb: 1 }} />
                  <Typography sx={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
                    No dwells found
                  </Typography>
                </Box>
              ) : (
                pagedDwells.map((dwell) => {
                  const isSelected = selectedDwell?.id === dwell.id;
                  const entityColor = getEntityColor(dwell);

                  return (
                    <ListItem key={dwell.id} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleDwellClick(dwell)}
                        sx={{
                          borderRadius: 2,
                          border: isSelected ? `2px solid ${primaryColor}` : `1px solid ${colors.border}`,
                          background: isSelected 
                            ? isDark ? `rgba(${primaryRgb}, 0.15)` : `rgba(${primaryRgb}, 0.08)`
                            : isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                          transition: 'all 0.15s',
                          '&:hover': { 
                            background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                          },
                          py: 0.8,
                          px: 1.5,
                        }}
                      >
                        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {/* Entity badge */}
                          <Chip
                            label={dwell.entity_id || dwell.device_id}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              bgcolor: `${entityColor}18`,
                              color: entityColor,
                              border: `1px solid ${entityColor}40`,
                              minWidth: 80,
                            }}
                          />

                          {/* Info column */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* Place name */}
                            {dwell.place_name && (
                              <Typography sx={{ color: colors.text, fontSize: '0.75rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {dwell.place_name}
                              </Typography>
                            )}
                            <Typography sx={{ color: colors.textSecondary, fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTime sx={{ fontSize: 12, opacity: 0.6 }} />
                              {(parseBackendDateTime(dwell.start_time) ?? new Date(dwell.start_time)).toLocaleString('en-US', { 
                                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                              })}
                              {!dwell.place_name && dwell.latitude && (
                                <span style={{ opacity: 0.5 }}> ({dwell.latitude.toFixed(3)}, {dwell.longitude.toFixed(3)})</span>
                              )}
                            </Typography>
                          </Box>

                          {/* Duration */}
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#ffb74d', whiteSpace: 'nowrap' }}>
                            {formatDuration(dwell.duration_seconds)}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  );
                })
              )}
            </List>
            {dwellCount > 0 && (
              <Box sx={{ borderTop: `1px solid ${colors.border}`, flexShrink: 0 }}>
                <TablePagination
                  component="div"
                  count={dwellCount}
                  page={safePage}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Per page:"
                  sx={{
                    minHeight: 40,
                    color: colors.textSecondary,
                    '& .MuiTablePagination-toolbar': { minHeight: 40, pl: 1, pr: 1 },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.65rem',
                    },
                    '& .MuiTablePagination-select': { fontSize: '0.7rem' },
                    '& .MuiIconButton-root': { color: colors.textSecondary },
                  }}
                />
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default DwellTab;
